import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, ChevronDown, ChevronUp, FileText, AlertCircle } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const POLICY_TYPES = [
  { type: 'terms_conditions', title: 'Terms & Conditions' },
  { type: 'privacy_policy', title: 'Privacy Policy' },
  { type: 'refund_policy', title: 'Refund & Cancellation Policy' },
  { type: 'return_policy', title: 'Return Policy' },
  { type: 'shipping_policy', title: 'Shipping Policy' }
];

export default function PoliciesTab() {
  const [selectedPolicy, setSelectedPolicy] = useState('terms_conditions');
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchPolicy(selectedPolicy);
  }, [selectedPolicy]);

  const fetchPolicy = async (policyType) => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/policies/${policyType}`);
      setPolicy(response.data);
      // Expand all sections by default
      const expanded = {};
      response.data.sections?.forEach((_, index) => {
        expanded[index] = true;
      });
      setExpandedSections(expanded);
    } catch (error) {
      toast.error('Failed to load policy');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.post(`/admin/policies/${selectedPolicy}`, policy);
      toast.success('Policy saved successfully');
    } catch (error) {
      toast.error('Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleInitializeDefaults = async () => {
    try {
      await api.post('/admin/policies/initialize-defaults');
      toast.success('Default policies initialized');
      fetchPolicy(selectedPolicy);
    } catch (error) {
      toast.error('Failed to initialize defaults');
    }
  };

  const addSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      title: '',
      content: '',
      order: policy.sections?.length || 0,
      is_active: true
    };
    setPolicy({
      ...policy,
      sections: [...(policy.sections || []), newSection]
    });
    setExpandedSections({
      ...expandedSections,
      [policy.sections?.length || 0]: true
    });
  };

  const updateSection = (index, field, value) => {
    const updatedSections = [...policy.sections];
    updatedSections[index] = {
      ...updatedSections[index],
      [field]: value
    };
    setPolicy({ ...policy, sections: updatedSections });
  };

  const removeSection = (index) => {
    const updatedSections = policy.sections.filter((_, i) => i !== index);
    setPolicy({ ...policy, sections: updatedSections });
    toast.info('Section removed. Save to apply changes.');
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= policy.sections.length) return;
    
    const updatedSections = [...policy.sections];
    [updatedSections[index], updatedSections[newIndex]] = [updatedSections[newIndex], updatedSections[index]];
    
    // Update order values
    updatedSections.forEach((section, i) => {
      section.order = i;
    });
    
    setPolicy({ ...policy, sections: updatedSections });
  };

  const toggleSection = (index) => {
    setExpandedSections({
      ...expandedSections,
      [index]: !expandedSections[index]
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3a5c]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="policies-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-bold text-[#1a3a5c]">Legal Policies</h2>
          <p className="text-gray-600 text-sm mt-1">Manage terms, privacy policy, and other legal documents</p>
        </div>
        <button
          onClick={handleInitializeDefaults}
          className="text-sm text-[#1a3a5c] hover:text-[#C9A967] transition-colors underline"
        >
          Initialize Default Policies
        </button>
      </div>

      {/* Policy Type Selector */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Policy to Edit</label>
        <div className="flex flex-wrap gap-2">
          {POLICY_TYPES.map((p) => (
            <button
              key={p.type}
              onClick={() => setSelectedPolicy(p.type)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedPolicy === p.type
                  ? 'bg-[#1a3a5c] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`select-policy-${p.type}`}
            >
              {p.title}
            </button>
          ))}
        </div>
      </div>

      {/* Policy Editor */}
      {policy && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {/* Policy Title */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Policy Title</label>
            <input
              type="text"
              value={policy.title || ''}
              onChange={(e) => setPolicy({ ...policy, title: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="Enter policy title"
              data-testid="policy-title-input"
            />
          </div>

          {/* Active Toggle */}
          <div className="mb-6 flex items-center">
            <input
              type="checkbox"
              id="policy-active"
              checked={policy.is_active !== false}
              onChange={(e) => setPolicy({ ...policy, is_active: e.target.checked })}
              className="h-4 w-4 text-[#1a3a5c] focus:ring-[#1a3a5c] border-gray-300 rounded"
            />
            <label htmlFor="policy-active" className="ml-2 text-sm text-gray-700">
              Policy is active and visible to customers
            </label>
          </div>

          {/* Sections */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Policy Sections</h3>
              <button
                onClick={addSection}
                className="flex items-center gap-2 px-3 py-1.5 bg-[#C9A967] text-white rounded-md text-sm hover:bg-[#b8954d] transition-colors"
                data-testid="add-section-btn"
              >
                <Plus className="h-4 w-4" />
                Add Section
              </button>
            </div>

            {policy.sections?.length === 0 && (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No sections yet</p>
                <button
                  onClick={addSection}
                  className="text-[#1a3a5c] hover:text-[#C9A967] text-sm font-medium"
                >
                  + Add your first section
                </button>
              </div>
            )}

            <div className="space-y-4">
              {policy.sections?.map((section, index) => (
                <div
                  key={section.id || index}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Section Header */}
                  <div
                    className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer"
                    onClick={() => toggleSection(index)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 font-mono">#{index + 1}</span>
                      <span className="font-medium text-gray-700">
                        {section.title || 'Untitled Section'}
                      </span>
                      {!section.is_active && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">
                          Hidden
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); moveSection(index, 'up'); }}
                        disabled={index === 0}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); moveSection(index, 'down'); }}
                        disabled={index === policy.sections.length - 1}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeSection(index); }}
                        className="p-1 hover:bg-red-100 text-red-500 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {expandedSections[index] ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Section Content */}
                  {expandedSections[index] && (
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Section Title
                        </label>
                        <input
                          type="text"
                          value={section.title || ''}
                          onChange={(e) => updateSection(index, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                          placeholder="Enter section title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Content (HTML supported)
                        </label>
                        <textarea
                          value={section.content || ''}
                          onChange={(e) => updateSection(index, 'content', e.target.value)}
                          rows={8}
                          className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] font-mono text-sm"
                          placeholder="Enter section content (HTML tags supported: <p>, <ul>, <li>, <strong>, etc.)"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          You can use HTML tags like &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;em&gt; for formatting
                        </p>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`section-active-${index}`}
                          checked={section.is_active !== false}
                          onChange={(e) => updateSection(index, 'is_active', e.target.checked)}
                          className="h-4 w-4 text-[#1a3a5c] focus:ring-[#1a3a5c] border-gray-300 rounded"
                        />
                        <label htmlFor={`section-active-${index}`} className="ml-2 text-sm text-gray-700">
                          Section is visible
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1a3a5c] to-[#0d2240] text-white rounded-sm hover:from-[#0d1e33] hover:to-[#102a4a] transition-colors disabled:opacity-50"
              data-testid="save-policy-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Policy</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium mb-1">Policy Management Tips</p>
          <ul className="list-disc list-inside space-y-1 text-blue-600">
            <li>Use HTML tags for formatting: &lt;p&gt; for paragraphs, &lt;ul&gt;&lt;li&gt; for lists</li>
            <li>Reorder sections using the up/down arrows</li>
            <li>Hide sections temporarily by unchecking "Section is visible"</li>
            <li>The policy link will appear on the checkout page</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
